/* ============================================================
   V5 极简画像页 —— 注册后单页采集 4 字段画像（§7.4）
   ------------------------------------------------------------
   写入 UserProfile：nickname / position / industry / ai_usage_purpose
   全部可跳过；信息仅用于 AI 出题场景定制，不参与测评打分。
   旧 V4 三段式（基础信息→工作问卷→分类结果）整体作废，Phase G/A
   字段已从模型删除。
   ============================================================ */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import client from '../api/client';
import './ProfileSetupPage.css';

// 常用行业下拉 —— 末项「其他」走自由文本兜底
const INDUSTRIES = [
  '互联网 / IT 软件',
  '金融 / 银行 / 保险',
  '教育 / 培训',
  '医疗 / 健康',
  '制造业',
  '零售 / 电商 / 消费',
  '房地产 / 建筑',
  '媒体 / 文化 / 娱乐',
  '政府 / 公共事业 / 非营利',
  '专业服务（咨询 / 法律 / 财会）',
  '能源 / 环保',
  '交通 / 物流',
  '农业',
];
const OTHER = '__other__';

export default function ProfileSetupPage() {
  const navigate = useNavigate();

  const [nickname, setNickname] = useState('');
  const [position, setPosition] = useState('');
  const [industrySelect, setIndustrySelect] = useState('');
  const [industryOther, setIndustryOther] = useState('');
  const [aiPurpose, setAiPurpose] = useState('');

  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // 预填已有画像（用户重复进入时）
  useEffect(() => {
    let alive = true;
    client.get('/profile/')
      .then(({ data }) => {
        if (!alive) return;
        setNickname(data.nickname || '');
        setPosition(data.position || '');
        setAiPurpose(data.ai_usage_purpose || '');
        const ind = data.industry || '';
        if (!ind) {
          setIndustrySelect('');
        } else if (INDUSTRIES.includes(ind)) {
          setIndustrySelect(ind);
        } else {
          setIndustrySelect(OTHER);
          setIndustryOther(ind);
        }
      })
      .catch(() => { /* 未填过画像是正常情况 */ })
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
  }, []);

  const resolvedIndustry = industrySelect === OTHER ? industryOther.trim() : industrySelect;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await client.put('/profile/', {
        nickname: nickname.trim(),
        position: position.trim(),
        industry: resolvedIndustry,
        ai_usage_purpose: aiPurpose.trim(),
      });
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || '保存失败，请稍后再试');
      setSaving(false);
    }
  };

  const handleSkip = () => navigate('/');

  if (fetching) {
    return (
      <div className="v5-profile-setup">
        <div className="vp-loading">加载中…</div>
      </div>
    );
  }

  return (
    <div className="v5-profile-setup">
      <div className="vp-shell">
        <header className="vp-head">
          <span className="vp-logo">AIQ</span>
          <h1>完善你的画像</h1>
          <p className="vp-sub">
            这些信息用于为你定制更贴近实际工作的测评题目，<b>不参与测评打分</b>。
            填得越具体，AI 出题越贴合你的真实场景 —— 也可以直接跳过。
          </p>
        </header>

        <div className="vp-card">
          {error && <div className="vp-error">{error}</div>}

          {/* 昵称 */}
          <div className="vp-field">
            <label className="vp-label" htmlFor="vp-nickname">
              昵称<span className="vp-tag optional">选填</span>
            </label>
            <input
              id="vp-nickname"
              className="vp-input"
              type="text"
              value={nickname}
              maxLength={20}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="报告里对你的称呼，留空则显示「匿名测评者」"
            />
          </div>

          {/* 岗位 */}
          <div className="vp-field">
            <label className="vp-label" htmlFor="vp-position">
              岗位<span className="vp-tag suggest">建议填写</span>
            </label>
            <input
              id="vp-position"
              className="vp-input"
              type="text"
              value={position}
              maxLength={50}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="如：产品经理、后端工程师、市场专员"
            />
          </div>

          {/* 行业 */}
          <div className="vp-field">
            <label className="vp-label" htmlFor="vp-industry">
              行业<span className="vp-tag suggest">建议填写</span>
            </label>
            <select
              id="vp-industry"
              className="vp-input vp-select"
              value={industrySelect}
              onChange={(e) => setIndustrySelect(e.target.value)}
            >
              <option value="">请选择所在行业</option>
              {INDUSTRIES.map((ind) => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
              <option value={OTHER}>其他（手动填写）</option>
            </select>
            {industrySelect === OTHER && (
              <input
                className="vp-input vp-input-other"
                type="text"
                value={industryOther}
                maxLength={50}
                onChange={(e) => setIndustryOther(e.target.value)}
                placeholder="请填写你所在的行业"
              />
            )}
          </div>

          {/* AI 使用目的 */}
          <div className="vp-field">
            <label className="vp-label" htmlFor="vp-purpose">
              AI 使用目的<span className="vp-tag strong">强烈建议</span>
            </label>
            <textarea
              id="vp-purpose"
              className="vp-input vp-textarea"
              value={aiPurpose}
              maxLength={300}
              rows={3}
              onChange={(e) => setAiPurpose(e.target.value)}
              placeholder="用 2-3 句话说说你主要用 AI 做什么，如：写周报和方案、查资料、做数据分析、辅助写代码…"
            />
            <div className="vp-hint">
              这是最影响出题质量的一项 —— 越具体，题目越贴近你的日常工作。
            </div>
          </div>
        </div>

        <div className="vp-actions">
          <button type="button" className="vp-btn ghost" onClick={handleSkip} disabled={saving}>
            跳过
          </button>
          <button type="button" className="vp-btn primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中…' : '保存并继续 →'}
          </button>
        </div>
      </div>
    </div>
  );
}
